import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonWhileLoops = () => {
    return (
        <div className="tutorial-content-module">
            <div className="premium-header">
                <h1>Mastering While Loops</h1>
                <p className="subtitle">The engine of indefinite iteration, event loops, and state machines.</p>
            </div>

            <div className="content-section">
                <h2>The Logic of "While"</h2>
                <p>
                    If <code>for</code> loops are about iterating over <em>known data</em> (a list, a range, a file),
                    then <code>while</code> loops are about iterating over <em>unknown states</em>.
                </p>
                <p>
                    Imagine you are teaching a robot to walk. You cannot say "Move leg 10 times" because you don't know if there is a wall.
                    You say "Move leg <strong>while</strong> path is clear". The loop depends on the <em>environment</em>, not a counter.
                </p>
                <p>
                    A `while` loop is essentially a repeated conditional statement. It keeps running as long as a boolean expression remains <code>True</code>.
                    This makes it the perfect tool for dynamic scenarios where the stopping condition is unpredictable:
                </p>
                <ul>
                    <li>Waiting for a user to type "exit".</li>
                    <li>Waiting for a server to respond.</li>
                    <li>Running a game engine until "Game Over".</li>
                    <li>Converging on a mathematical solution.</li>
                </ul>

                <CodeBlock language="python" code={`# Syntax:
# while condition:
#     do_something()

fuel = 10
while fuel &gt; 0:
    print(f"Driving... Fuel: {fuel}")
    fuel -= 1 #  Crucial: Modify state to avoid infinite loop!

print("Out of gas.")`} />
            </div>

            <div className="content-section">
                <h2>The Infinite Loop: Feature or Bug?</h2>
                <p>
                    In your first CS class, infinite loops are treated as bugs. "My program froze!"
                    But in the real world, infinite loops are the heartbeat of almost every piece of software you use.
                </p>

                <h3>The "Event Loop" Pattern</h3>
                <p>
                    Web servers, game engines, and UI frameworks all run inside a <code>while True</code> loop.
                    They sleep until an event happens (click, request, keypress), handle it, and loop again.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>A Simple Command Shell</span>
                    </div>
                    <CodeBlock language="python" code={`#  The "Game Loop" Pattern
import time

status = "Running"

while True: # loop forever
    command = input("System Ready &gt; ")
    
    if command == "exit":
        print("Shutting down...")
        break #  The only way out!
    
    elif command == "status":
        print(f"System is {status}")
        
    else:
        print("Unknown command.")
        
    time.sleep(0.1) # Good practice: Don't hog CPU`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Control Flow: Taking Command</h2>
                <p>
                    Sometimes the loop condition (`while x &gt; 0`) isn't enough. You need surgical control over the flow.
                </p>

                <div className="comparison-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Keyword</th>
                                <th>Description</th>
                                <th>Common Use Case</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>break</code></td>
                                <td>Immediately terminates the loop.</td>
                                <td>"Found what I need, stop looking."</td>
                            </tr>
                            <tr>
                                <td><code>continue</code></td>
                                <td>Jumps to the top of the loop (check condition).</td>
                                <td>"Invalid data, skip this one."</td>
                            </tr>
                            <tr>
                                <td><code>else</code></td>
                                <td>Runs <strong>only if</strong> the loop ends normally (no `break`).</td>
                                <td>"Search completed, nothing found."</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <h3>Using `continue` to Reduce Nesting</h3>
                <p>
                    Instead of nesting giant `if` blocks, use `continue` to skip invalid states early ("Guard Clause").
                </p>
                <CodeBlock language="python" code={`#  Nested Spaghetti
while data_stream:
    packet = get_packet()
    if packet.is_valid():
        if not packet.is_empty():
            process(packet)

# Flat & Clean
while data_stream:
    packet = get_packet()
    
    if not packet.is_valid():
        continue
        
    if packet.is_empty():
        continue
        
    process(packet)`} />
            </div>

            <div className="content-section">
                <h2>The `else` Block: The "Search" Pattern</h2>
                <p>
                    Python loops have an `else` clause that runs <strong>only if the loop finishes successfully without hitting a `break`</strong>.
                    This is extremely useful for "search" algorithms.
                </p>
                <p>
                    Without `else`, you need a "flag" variable to track if you found what you were looking for.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>The "Flag" vs "Else" Pattern</span>
                    </div>
                    <CodeBlock language="python" code={`# Task: Check if a number is prime
n = 13
div = 2

#  The "Flag" Way (Verbose)
is_prime = True
while div < n:
    if n % div == 0:
        is_prime = False
        break
    div += 1

if is_prime:
    print(f"{n} is prime")

# The "Else" Way (Clean)
div = 2
while div < n:
    if n % div == 0:
        print(f"{n} is not prime")
        break
    div += 1
else:
    # Runs ONLY if we never broke out (loop finished naturally)
    print(f"{n} is prime")`} />
                </div>
            </div>

            <div className="content-section">
                <h2>The Walrus Operator (`:=`)</h2>
                <p>
                    Introduced in Python 3.8, assignment expressions allow you to assign a value <em>and</em> check it in the same line.
                    This removes the awkward "priming read" problem in `while` loops.
                </p>

                <CodeBlock language="python" code={`# Simulation: Initializing a buffer
import random
def get_data():
    return random.choice(["data", "data", ""]) # Returns empty string occasionally

#  The Old Way (Repetitive)
chunk = get_data()      # 1. Priming read
while chunk != "":      # 2. Check
    print(f"Processing: {chunk}")
    chunk = get_data()  # 3. Update read (Duplicated code!)

# The Walrus Way (Concise)
# Assigns chunk AND checks if it is truthy/empty
while (chunk := get_data()) != "":
    print(f"Processing: {chunk}")
    # No duplicated logic!`} />
            </div>

            <div className="content-section">
                <h2>Missing Feature: The Do-While Loop</h2>
                <p>
                    Many languages have a `do...while` loop that guarantees the code runs <em>at least once</em>.
                    Python does not. But you can emulate it easily.
                </p>
                <CodeBlock language="python" code={`# Java / C++ Style (Conceptual)
# do {
#    action()
# } while (condition);

# Python Style (Idiomatic)
while True:
    password = input("Enter password: ")
    if len(password) >= 8:
        break # Exit condition at the end!
    print("Too short. Try again.")`} />
            </div>

            <div className="content-section common-pitfalls">
                <h2>The Infinite Nightmare: Common Pitfalls</h2>

                <div className="pitfall-card">
                    <h3> Floating Point Comparisons</h3>
                    <p><strong>The Trap:</strong> Floats are not exact. Loop counters using floats might never hit the exact target.</p>
                    <CodeBlock language="python" code={`#  Runs Forever!
x = 0.0
while x != 1.0: # 1.0 might be 0.999999 or 1.000001
    print(x)
    x += 0.1 

# Fix: Use comparisons (<, >) or integers
while x < 1.0: ...`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Real World Application: Proper Retry Logic</h2>
                <p>
                    The most common use of `while` loops in backend systems is <strong>retrying failed operations</strong>.
                    Network calls, database queries, and API requests are unreliable by nature. They fail.
                    If you just give up immediately, your application is brittle.
                </p>
                <p>
                    But you cannot just retry instantly in a tight loop. If a database is overloaded, hitting it with 1000 instant retries
                    will effectively DDoS (Distributed Denial of Service) your own infrastructure. This is known as the "Thundering Herd" problem.
                </p>
                <p>
                    The solution is <strong>Exponential Backoff</strong>. You wait 1 second, then 2, then 4, then 8.
                    This gives the failing system time to recover.
                </p>
                <CodeBlock language="python" code={`import time
import random

def connect_to_db():
    # Simulate 80% failure rate
    if random.random() < 0.8:
        raise ConnectionError("DB Down!")
    print("Connected!")
    return True

max_retries = 5
attempt = 0
wait_time = 1 # Seconds

while attempt < max_retries:
    try:
        print(f"Attempt {attempt + 1}...")
        connect_to_db()
        break # Success! Exit loop.
    except ConnectionError:
        print(f"Failed. Retrying in {wait_time}s...")
        time.sleep(wait_time)
        wait_time *= 2 # Double the wait time (Exponential Backoff)
        attempt += 1
else:
    # Runs if we hit max_retries without breaking
    print(" Fatal Error: Could not connect to DB after 5 attempts.")`} />
            </div>

            <div className="content-section">
                <h2>Performance Deep Dive: Under the Hood</h2>
                <p>
                    A common interview question asked at top tech companies is: <strong>"Which is faster? A `while` loop or a `for` loop?"</strong>
                    The answer reveals whether you understand how Python works internally.
                </p>
                <p>
                    In Python, the `for` loop is almost always faster. To understand why, we need to look at the <strong>CPython Interpreter</strong>.
                    Python is an interpreted language, meaning every instruction you write is translated into "bytecode" which is then executed by a virtual machine.
                </p>
                <p>
                    When you run a `while` loop, Python has to execute a series of bytecode instructions for <em>every single iteration</em>:
                    it must load the variable, load the comparison constant, perform the comparison, jump if false, execute the body, load the variable again, add one, and store it back.
                    That is a lot of overhead!
                </p>
                <p>
                    In contrast, a `for` loop uses the <strong>Iterator Protocol</strong>. When you say `for i in range(1000)`, Python calls a C-function inside the `range` object.
                    The looping mechanism, the incrementing, and the boundary checking all happen inside highly optimized C code, not Python bytecode.
                    This "Loop Unrolling" at the C level provides a significant speed boost.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Bytecode Proof (dis module)</span>
                    </div>
                    <CodeBlock language="python" code={`import dis

def loop_while():
    i = 0
    while i < 1000:
        i += 1

def loop_for():
    for i in range(1000):
        pass

# Disassemble 'loop_while'
# LOAD_FAST (i)
# LOAD_CONST (1000)
# COMPARE_OP (<)
# POP_JUMP_IF_FALSE
# ... (Instructions run 1000 times)

# Disassemble 'loop_for'
# FOR_ITER
# STORE_FAST (i)
# ... (Only 2 instructions per loop!)

# Result: 'for' loops are typically 1.5x - 2x faster for simple counting.`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Algorithm Mastery: Recursion to Iteration</h2>
                <p>
                    Python has a strict recursion limit (usually 1000 frames). If you write a recursive algorithm for a deep tree, you will crash with `RecursionError`.
                </p>
                <p>
                    The solution? <strong>Convert recursion to a `while` loop using an explicit stack.</strong>
                </p>

                <CodeBlock language="python" code={`# Task: Traverse a deep directory structure

#  Recursive (Crashes on deep folders)
def walk_recursive(path):
    print(path)
    for child in path.iterdir():
        walk_recursive(child)

# Iterative (Robust & Crash-proof)
def walk_iterative(start_path):
    # 1. Initialize a stack with the starting point
    stack = [start_path]
    
    # 2. Loop while there is work to do
    while stack:
        current = stack.pop() # Get last item (LIFO)
        print(current)
        
        # 3. Add children to the stack manually
        try:
            for child in current.iterdir():
                stack.append(child)
        except PermissionError:
            continue

# This can handle infinite depth (limited only by RAM)`} />
            </div>

            <div className="content-section">
                <h2>Advanced Systems: Building an Event Loop</h2>
                <p>
                    If you successfully understood this section, you are in the top 1% of Python learners.
                    We are going to demystify one of the most complex topics in programming: <strong>Concurrency</strong>.
                </p>
                <p>
                    JavaScript developers are familiar with the "Event Loop" that powers the browser and Node.js.
                    Python's `asyncio` module works the same way. But how does it actually function?
                    It is not magic. It is, quite literally, a simple `while` loop managing a list of tasks.
                </p>
                <p>
                    The concept is called <strong>Cooperative Multitasking</strong>. Unlike threads, where the operating system brutally interrupts code to switch contexts (Preemptive Multitasking),
                    in a cooperative system, the function <em>voluntarily</em> pauses itself to let others run.
                    In Python, the tool for "pausing" is the <code>yield</code> keyword (Generators).
                </p>
                <p>
                    Let's build a fully functional concurrent system from scratch using nothing but a `while` loop and a `deque` (double-ended queue).
                </p>

                <CodeBlock language="python" code={`from collections import deque
import time

# 1. The "Task Queue"
tasks = deque()

def add_task(task):
    tasks.append(task)

def run_event_loop():
    print(" System Starting...")
    while tasks: # Loop while there is work
        task = tasks.popleft() # Get next task
        
        try:
            # Run the generator until next yield
            next(task)
            # If it yielded, put it back in the queue
            tasks.append(task)
        except StopIteration:
            # Task finished
            print("Task Completed")

# 2. Define some "Async" tasks
def task_one():
    print("Task 1: Start")
    yield
    print("Task 1: Working...")
    yield
    print("Task 1: End")

def task_two():
    print("Task 2: Start")
    yield
    print("Task 2: End")

# 3. Schedule and Run
add_task(task_one())
add_task(task_two())

run_event_loop()

# Output (Interleaved!):
# Task 1: Start
# Task 2: Start
# Task 1: Working...
# Task 2: End
# Task 1: End`} />
                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Architecture Note:</strong> This is exactly how Python's <code>asyncio</code> and Node.js work under the hood.
                        A single <code>while</code> loop switching between tasks that are waiting for I/O.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>Design Pattern: The Finite State Machine (FSM)</h2>
                <p>
                    One of the most powerful uses of a `while` loop is implementing a <strong>Finite State Machine</strong>.
                    This is the standard architecture for:
                </p>
                <ul>
                    <li>Game Logic (Idle -&gt; Run -&gt; Jump -&gt; Idle)</li>
                    <li>Order Processing (New -&gt; Paid -&gt; Shipped -&gt; Delivered)</li>
                    <li>Network Handshakes (Syn -&gt; SynAck -&gt; Ack)</li>
                </ul>
                <p>
                    Instead of a mess of recursive functions or nested `if` statements, you use a `while` loop to transition between states.
                </p>

                <CodeBlock language="python" code={`# Simulation: Vending Machine Logic

state = "IDLE"
balance = 0
price = 100

print("System Online. States: IDLE, INSERT_MONEY, DISPENSE, REFUND")

while True:
    if state == "IDLE":
        cmd = input("Insert Coin? (y/n) &gt; ")
        if cmd == "y":
            state = "INSERT_MONEY"
        else:
            time.sleep(1)

    elif state == "INSERT_MONEY":
        amount = int(input("Amount ($) &gt; "))
        balance += amount
        print(f"Balance: {balance}")
        
        if balance >= price:
            state = "DISPENSE"
        else:
            print(f"Need {price - balance} more.")

    elif state == "DISPENSE":
        print(" Dispensing Soda...")
        balance -= price
        time.sleep(2)
        if balance &gt; 0:
            state = "REFUND"
        else:
            state = "IDLE"

    elif state == "REFUND":
        print(f"Returning change: {balance}")
        balance = 0
        state = "IDLE"

# This loop runs forever, cleanly managing transitions.`} />
            </div>

            <div className="content-section">
                <h2>Theory: Threading vs AsyncIO</h2>
                <p>
                    We built an event loop earlier. But Python also has "Threads".
                    When should you use `while` loop concurrency (`asyncio`) vs Operating System Threads (`threading`)?
                </p>

                <h3>The Challenge: The GIL</h3>
                <p>
                    CPython has a mutex called the <strong>Global Interpreter Lock (GIL)</strong>.
                    It prevents multiple threads from executing Python bytecode at the exact same time on different CPU cores.
                    This means Python Threads provides <em>concurrency</em> (interleaving), but not <em>parallelism</em> (simultaneous execution) for CPU tasks.
                </p>

                <div className="comparison-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Feature</th>
                                <th>Threading</th>
                                <th>AsyncIO (Event Loop)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><strong>Mechanism</strong></td>
                                <td>OS Preemptive Multitasking</td>
                                <td>Cooperative Multitasking (`while` loop)</td>
                            </tr>
                            <tr>
                                <td><strong>Overhead</strong></td>
                                <td>High (Memory per thread, context switching)</td>
                                <td>Low (Just a generator object)</td>
                            </tr>
                            <tr>
                                <td><strong>Best For</strong></td>
                                <td>Blocking I/O (Legacy libs) or background tasks</td>
                                <td>High-concurrency Networking (WebScrapers, APIs)</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="content-section">
                <h2>Capstone Project: Building a Load Balancer</h2>
                <p>
                    Let's combine everything we've learned (Infinite Loops, Helper Functions, Time management) to build a
                    <strong>Round-Robin Load Balancer</strong> simulation.
                </p>
                <p>
                    A load balancer sits in front of servers and distributes traffic evenly. It runs forever (`while True`),
                    checks server health, and forwards requests.
                </p>

                <CodeBlock language="python" code={`import time
import random
import { ProTip } from '../../components/LessonUI';
import { ProTip } from '../../components/LessonUI';

servers = ["Server-1", "Server-2", "Server-3"]
queue = []

def get_incoming_requests():
    # Simulate random traffic spikes
    if random.random() &gt; 0.7:
        return [f"Req-{random.randint(1000,9999)}" for _ in range(random.randint(1, 5))]
    return []

def process_queue():
    while queue: # Process until empty
        # 1. Get request (FIFO)
        req = queue.pop(0)
        
        # 2. Pick server (Round Robin)
        # We perform a "rotation" by moving the top server to the bottom
        server = servers.pop(0)
        servers.append(server)
        
        print(f"Route {req} -&gt; {server}")
        time.sleep(0.2) # Simulate latency

print(" Load Balancer Active...")
cycle = 0

while True:
    cycle += 1
    print(f"--- Cycle {cycle} ---")
    
    # 1. Ingest Traffic
    new_reqs = get_incoming_requests()
    if new_reqs:
        print(f" Received {len(new_reqs)} new requests.")
        queue.extend(new_reqs)
    
    # 2. Process Traffic
    if queue:
        process_queue()
    else:
        print(" Idle...")
        
    # 3. Prevent CPU Burn
    time.sleep(1)`} />
            </div>

            <div className="content-section">
                <h2>Pro Tip: The `iter()` Sentinel Trick</h2>
                <p>
                    Here is a trick that will make senior developers nod in approval.
                    You can replace a clumsy `while` loop with a clean `for` loop using the two-argument version of `iter()`.
                </p>
                <p>
                    <strong>Syntax:</strong> `iter(callable, sentinel)`
                    It calls `callable()` repeatedly until it returns `sentinel`.
                </p>

                <CodeBlock language="python" code={`# Task: Read a file in 64-byte chunks until empty

#  Standard While Loop
with open("data.bin", "rb") as f:
    while True:
        chunk = f.read(64)
        if chunk == b"":
            break
        process(chunk)

# The Sentinel Trick
with open("data.bin", "rb") as f:
    # "Call f.read(64) until it returns b''"
    for chunk in iter(lambda: f.read(64), b""):
        process(chunk)

# Why use this?
# 1. It turns a 'while' loop into a 'for' loop (faster C-iteration).
# 2. It removes the explicit 'break' check.
# 3. It works with list comprehensions!
chunks = [c for c in iter(lambda: f.read(64), b"")]`} />
            </div>

            <div className="content-section">
                <h2>History Lesson: `while 1` vs `while True`</h2>
                <ProTip><p>
                        In old Python 2 codebases, you might see `while 1:` instead of `while True:`.
                        <strong>Why?</strong>
                        In Python 2, `True` was not a keyword. It was a global variable that could technically be reassigned (`True = 0` was legal!).
                        So, `while True` required a global variable lookup every iteration. `while 1` used a literal, which was faster.
                    </p>
                    <p>
                        <strong>Modern Python 3:</strong> `True` is a reserved keyword. The compiler optimizes `while True` into the exact same bytecode as `while 1`.
                        So, always use `while True` for readability.
                    </p></ProTip>
            </div>

            <div className="content-section">
                <h2>Summary: Best Practices Checklist</h2>
                <ProTip><ul>
                        <li><strong>Infinite Loops:</strong> Great for servers/games/retries, but always include a `break` or `sleep`.</li>
                        <li><strong>Walrus Operator:</strong> Use `while (x := get()):` to avoid code duplication.</li>
                        <li><strong>Performance:</strong> Prefer `for` loops for CPU tasks. Use `while` for I/O tasks.</li>
                        <li><strong>Recursion:</strong> If depth is &gt; 1000, replace recursion with a `while` loop + `stack`.</li>
                        <li><strong>Sentinels:</strong> Use `iter(func, sentinel)` to simplify read-loops.</li>
                    </ul></ProTip>
            </div>

        </div >
    );
};

export default PythonWhileLoops;

